import { useState, type ReactNode } from 'react';
import type { Entry, Fixture, Round } from '@h2h/shared';
import { RoundOverview } from '../../../../components/play/SlatePickFlow';
import { ChevronLeft, Lock } from '../../../../components/ui/icons';
import { useTeamSquads } from '../../../../hooks/useQueries';
import { useHaptics } from '../../../../hooks/useHaptics';
import { countdownTo } from '../../../../lib/format';
import { CARD_AT_RISK } from '../../../../lib/picks';
import { computeSlateLiveStats } from '../../../../lib/slateStats';
import { useNow } from '../../helpers';
import { useMakePicksDraft } from '../../../make-picks/useMakePicksDraft';
import { LiveFixtureSheet } from '../../../match-detail/LiveFixtureSheet';
import { RoundHeader } from '../RoundHeader';
import { CardBuilderSheet } from './CardBuilderSheet';

/**
 * The Build-Your-Card overview, inlined into the League Hub's Compete tab. It lists
 * every fixture in the round (projected score + per-card boosts) and opens the card
 * builder as a full-screen overlay — the old standalone "Build your cards" screen,
 * merged in so the hub is the one place you play. Mount one per round (`key`) so the
 * draft re-seeds when the player pages to a different round.
 */
export function BuildOverview({
  leagueId,
  round,
  fixtures,
  entry,
  uid,
  nationalTeams,
  scorerEnabled,
  statsEnabled,
  pager,
  fixtureFocus,
  onBoard,
  onClearFixtureFocus
}: {
  leagueId: string;
  round: Round;
  fixtures: Fixture[];
  entry: Entry | null | undefined;
  uid: string | null;
  nationalTeams: boolean;
  scorerEnabled: boolean;
  statsEnabled: boolean;
  pager?: ReactNode;
  /** Deep-link: open the card builder for this fixture on mount. */
  fixtureFocus?: string | null;
  onBoard: () => void;
  onClearFixtureFocus?: () => void;
}) {
  const now = useNow(true);
  const { impact } = useHaptics();
  const [liveFixtureId, setLiveFixtureId] = useState<string | null>(null);

  const teamIds = fixtures.flatMap((f) => [f.homeTeamId, f.awayTeamId]);
  const { data: squads } = useTeamSquads(scorerEnabled ? leagueId : undefined, teamIds);

  const draft = useMakePicksDraft({
    leagueId,
    roundId: round.roundId,
    round,
    fixtures,
    entry,
    uid,
    now,
    scorerEnabled,
    squads,
    fixtureFocus
  });

  const closeBuilder = () => {
    draft.closeBuilder();
    onClearFixtureFocus?.();
  };

  // A fixture tap is mode-aware: a still-buildable match opens the inline card
  // builder sheet; one that has kicked off / gone live / settled opens the live
  // match view as a slide-up sheet over the hub (no route push → no remount, no
  // janky to-and-back transition) — so a LIVE match is tappable and watchable.
  const openFixture = (fixtureId: string) => {
    const f = fixtures.find((x) => x.fixtureId === fixtureId);
    if (!f) return;
    if (f.status === 'SCHEDULED' && now < f.kickoffAt) {
      draft.openBuilder(fixtureId);
      return;
    }
    impact('light');
    setLiveFixtureId(fixtureId);
  };

  if (!draft.seeded) {
    return (
      <div className="flex h-40 items-center justify-center">
        <span className="h-[9px] w-[9px] animate-h2h-pulse rounded-full bg-accent" />
      </div>
    );
  }

  if (draft.newEntryClosed) {
    return (
      <>
        {pager}
        <div className="mt-3 rounded-[20px] border border-black/[0.07] bg-white px-[18px] py-6 text-center shadow-[0_18px_40px_-26px_rgba(20,20,50,0.34)]">
          <div className="mx-auto mb-3 grid h-[42px] w-[42px] place-items-center rounded-[13px] bg-night">
            <Lock width={20} height={20} strokeWidth={2.2} className="text-white" />
          </div>
          <div className="text-[15px] font-extrabold tracking-snug text-ink">Cards closed for this round</div>
          <p className="mx-auto mt-1.5 max-w-[280px] text-[12.5px] font-medium leading-[1.45] text-ink2">
            Every match has kicked off, so there&apos;s nothing left to build. A fresh round opens next match-day.
          </p>
          <BoardButton onClick={onBoard} />
        </div>
      </>
    );
  }

  const cardsBuilt = draft.startedCards.length;
  const deadlineLabel =
    draft.nextLockAt != null && !draft.frozen ? countdownTo(draft.nextLockAt, now) : null;
  const slateStats = computeSlateLiveStats(draft.startedCards, draft.sortedFixtures, entry, now);

  return (
    <>
      {pager}

      {cardsBuilt > 0 ? (
        <RoundHeader
          state="building"
          cardsBuilt={cardsBuilt}
          total={draft.sortedFixtures.length}
          deadlineLabel={deadlineLabel}
          {...(slateStats.hasStarted
            ? {
                mode: 'live' as const,
                banked: slateStats.banked,
                inPlay: slateStats.inPlay,
                remainingToWin: slateStats.remainingToWin,
                settledCount: slateStats.settledCount,
                liveCount: slateStats.liveCount
              }
            : {
                mode: 'projected' as const,
                toWin: draft.toWinTotal,
                atRisk: cardsBuilt * CARD_AT_RISK
              })}
        />
      ) : (
        <RoundHeader state="empty" deadlineLabel={deadlineLabel} />
      )}

      <RoundOverview
        fixtures={draft.sortedFixtures}
        cardByFixture={draft.cardByFixture}
        now={now}
        flag={nationalTeams}
        hideHeader
        onOpen={openFixture}
      />

      {draft.error && (
        <p className="mt-2 rounded-xl bg-miss/10 px-3 py-2 text-[13px] font-semibold text-danger">
          {draft.error}
        </p>
      )}

      {draft.frozen && (
        <div className="pt-1">
          <BoardButton onClick={onBoard} />
        </div>
      )}

      <CardBuilderSheet
        open={draft.inBuilder}
        fixture={draft.activeFixture}
        card={draft.activeCard}
        legCatalog={draft.activeFixture ? draft.legCatalogFor(draft.activeFixture.fixtureId) ?? [] : []}
        statsEnabled={statsEnabled}
        scorer={draft.activeFixture ? draft.scorerFor(draft.activeFixture) : null}
        flag={nationalTeams}
        frozen={draft.activeFrozen}
        pending={draft.autosave.isPending}
        onChange={draft.updateCard}
        onClose={closeBuilder}
      />

      <LiveFixtureSheet
        openFixtureId={liveFixtureId}
        onClose={() => setLiveFixtureId(null)}
        leagueId={leagueId}
        round={round}
        fixtures={fixtures}
        entry={entry}
        nationalTeams={nationalTeams}
      />
    </>
  );
}

function BoardButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="tap mt-4 flex w-full items-center justify-center gap-2 rounded-[15px] bg-[#F0F0F4] py-[15px] text-[14px] font-extrabold text-ink active:scale-[0.99]"
    >
      <ChevronLeft width={16} height={16} strokeWidth={2.4} />
      View live board
    </button>
  );
}
